import { AfterViewInit, Component, OnInit } from "@angular/core";
import {
  Calculation,
  CalculationPlan,
  CalculationPlanService,
  CalculationService,
  ListOfCalculations,
  Source,
  SourceQuestion,
  SourceService,
  SourceWithSourceQuestions,
  TaxReturnType,
  TaxReturnSubType,
} from "../../../../../swaggerServices";
import { ActivatedRoute } from "@angular/router";
import { CalcPlan } from "../../calc-plan-editor/model/calc-plan";
import { SubtypeMode, TaxReturnSourceOptions } from "../../calc-plan-editor/model/calc-input";
import { of, forkJoin, Subscription } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { sortBy } from "lodash-es";
import moment from "moment";
import { endBlock } from "../../calc-plan-editor/model/program";
import { DropdownListOption } from "../../../../shared/components/search-dropdown/search-dropdown.component";
import { TaxReturnTypeSubTypeService } from "../../../../shared/util/tax-return-type-subtype.service";
import { UntypedFormControl } from "@angular/forms";
import { TypeSubtypeLookup, SubtypeChain, generateSubtypeChains, subtypeChainToDropdownListOption, subtypeToDropdownListOptions, typeToDropdownListOptions } from "../../../../shared/util/tax-return-type-subtype";
import { ConfigStateService } from "../../../config/config-state.service";
import { TenantConfigService } from "../../../../shared/services/tenant-config.service";

declare var $: any;

const TYPE_SUBTYPE_SAME = null; // deliberately null as its similar to what the backend expects
const SUBTYPE_PRIOR = "prior"; // lower-case string as its similar to what the backend expects

export interface ISourceQuestion extends SourceQuestion {
  inGroup?: boolean;
  isTableColumn?: boolean;
  trailStr?: string;
  isAnswerGroup?: boolean;
  isGlobal?: boolean;
  taxReturnTypeId?: number;
}
export interface ISource extends Source {
  sourceQuestions?: ISourceQuestion[];
}

@Component({
    selector: "app-calculation",
    templateUrl: "./calculation.component.html",
    styleUrls: ["./calculation.component.scss"],
    standalone: false
})
export class CalculationComponent implements OnInit, AfterViewInit {
  subscriptions: Subscription[] = [];
  dataSources: ISource[];
  selectedSource?: ISource;
  calcPlan?: CalcPlan;
  originalCalcPlan?: CalculationPlan;
  calcBlocks: Calculation[];
  fullBlocks: Calculation[];
  typeSubtypeLookup: TypeSubtypeLookup;
  trTypes: TaxReturnType[];
  trSubtypes: TaxReturnSubType[];
  trYears: DropdownListOption[] = [];
  trTypeOptions: DropdownListOption[] = [];
  trSubtypeOptions: DropdownListOption[] = [];
  trTypesCtrl: UntypedFormControl;
  trSubtypesCtrl: UntypedFormControl;
  trYearsCtrl: UntypedFormControl;
  questionYear: number;
  editUserandDate: string;
  taxReturnAlias: string;
  taxpayerAlias: string;
  uiConfig: TenantConfigService;
  draggingSource: boolean = false;

  isGlobalCalcPlan: boolean = false;

  constructor(
    private sourceService: SourceService,
    private calculationPlanService: CalculationPlanService,
    private activatedRoute: ActivatedRoute,
    private calculationService: CalculationService,
    public config: ConfigStateService,
    private taxReturnTypeSubTypeService: TaxReturnTypeSubTypeService,
  ) {}

  ngOnInit() {
    const questionId = this.activatedRoute.params["value"].id;

    const blocksObs = this.calculationService.getCalculations()
      .pipe(map((r: ListOfCalculations) => {
        return r.calculations;
      })).pipe(
        switchMap(blocks => {
          this.calcBlocks = sortBy(
            blocks.filter(b => b.id !== endBlock.id),
            b => b.name
          );
          this.fullBlocks = blocks;
          return calcPlanObs;
        }),
      );
      this.subscriptions.push(
        this.config.loadConfig().subscribe(config => {
          this.uiConfig = new TenantConfigService(config);
          this.taxReturnAlias = this.uiConfig.config.taxReturnAlias
          this.taxpayerAlias = this.uiConfig.config.taxpayerAlias
        })
      );

    const calcPlanObs = this.calculationPlanService
      .getCalculationPlans(questionId, true).pipe(
      map(({ calculationPlans, user }) => {
        const plan = calculationPlans && calculationPlans[0]; // expecting at most 1 plan per question (for now... drafts may or may not change that)
        if (!plan) {
          const error: any = new Error(
            `Calc plan for question id=${questionId} does not exist`
          );
          error.missingCalcPlan = true;
          throw error;
        }
        const lastUpdate = moment(plan.updatedAt).format('DD/MM/YYYY HH:mm').toString();
        this.editUserandDate = user
          ? `Last edited by: ${user.firstName} ${user.lastName} at ${lastUpdate}`
          : `Auto generated at ${lastUpdate}`;
        return plan;
      })).pipe(
        switchMap(cp => {
          this.originalCalcPlan = cp;
          this.isGlobalCalcPlan = cp.question.year === 1900 && !cp.question.taxReturnTypeId;
          const sourceObs = this.sourceService.getSourcesByyearIncludeSourceQuestions(cp.question.year);
          const typeSubtypeObs = this.taxReturnTypeSubTypeService.getAllTypesAndSubtypes(this.isGlobalCalcPlan);
          return forkJoin([sourceObs, typeSubtypeObs]);
        })
      );

    this.subscriptions.push(
      blocksObs.subscribe(
        ([sources, typeSubtypeLookup]) => {
          sources.forEach(s => {
            // filter out tables
            s.sourceQuestions = s.sourceQuestions.filter(
              sq => sq.dataType !== "table"
            );
            // configure trail
            s.sourceQuestions.forEach((sq: ISourceQuestion) => {
              const trail =
                sq.trail && sq.trail.length ? sq.trail.join(", ") : null;
              const arr = [`Q${sq.id}`];
              if (trail != null) {
                arr.push(trail);
              }
              sq.trailStr = arr.join(" - ");
            });
          });
          this.dataSources = sources;
          if (sources) {
            this.selectedSource = sources[0];
          }
          this.typeSubtypeLookup = typeSubtypeLookup;

          this.loadCalcPlan(this.originalCalcPlan);

          setTimeout(() => $(".ui.inline.dropdown").dropdown(), 0);

          this.trTypesCtrl = new UntypedFormControl('', {});
          this.trSubtypesCtrl = new UntypedFormControl('', {});
          this.trYearsCtrl = new UntypedFormControl('', {})

          this.loadTaxReturnSourceOptionsDropdowns(this.isGlobalCalcPlan);
        },
        e => {
          if (e.missingCalcPlan) {
            alert(e.message);
          }
        })
    );
  }

  loadCalcPlan(calcPlan: CalculationPlan) {
    const b: any = this.fullBlocks;
    this.calcPlan = CalcPlan.fromData(calcPlan, this.dataSources, b, this.typeSubtypeLookup, this.isGlobalCalcPlan);

    const taxReturnSource = this.dataSources.find(s => s.type === "taxReturn");
    if (taxReturnSource) {
      const q = taxReturnSource.sourceQuestions.find(
        sq => sq.id === this.calcPlan.question.id
      );
      if (q) {
        // The question from the sources has more data, i.e. inGroup and isTableColumn etc, so take that instead.
        this.calcPlan.question = q;
        this.questionYear = q.year;
      }
    }
  }

  selectSource(source: SourceWithSourceQuestions) {
    this.selectedSource = source;
  }

  get sourceQuestions() {
    if (!this.selectedSource) { return []; }

    let result = [];

    const sourceQuestions = this.selectedSource.sourceQuestions || [];
    if (this.selectedSource.type === "taxReturn") {
      // Filter questions based on year-taxReturnType selection
      const typeId = this.trTypesCtrl.value.value || null;
      const isPriorYear = (this.trYearsCtrl.value.value === 'prior year');
      const year = (isPriorYear ? (this.questionYear - 1) : this.questionYear);

      const sourceOption = this.sourceOptions(this.selectedSource);
      const currentYear = !sourceOption.priorYear;
      const sameType = !sourceOption.type;
      const sameSubtype = sourceOption.subtypeMode === SubtypeMode.Same;
      const includeGlobal = currentYear && sameType && sameSubtype;

      const filtered = sourceQuestions.filter((q :ISourceQuestion) => {
        if (q.isGlobal) {
          return includeGlobal;
        }
        return q.taxReturnTypeId.toString() === typeId.toString() && q.year === year;
      });
      result = filtered;
    } else {
      result = sourceQuestions;
    }

    return result;
  }

  ngAfterViewInit() {
    $(".ui.menu .item").tab();
  }

  prefix(source: SourceWithSourceQuestions, question: SourceQuestion) {
    return this.isGlobalCalcPlan && source.type !== "taxReturn" && source.type !== "metadata" ? `(${question.year}) ` : null;
  }

  sourceDragData(source: SourceWithSourceQuestions, question: SourceQuestion) {
    return { source, question, sourceOptions: this.sourceOptions(source), isGlobalCalcPlan: this.isGlobalCalcPlan };
  }

  newPlanLoaded(plan: string) {
    this.loadCalcPlan({ ...this.originalCalcPlan, plan });
  }

  sourceOptions(source): TaxReturnSourceOptions {
    if (source.type !== "taxReturn") { return null; }

    const priorYear = this.trYearsCtrl.value.value === 'prior year';
    const typeId = this.trTypesCtrl.value.value || null;
    const type: TaxReturnType = typeId ? this.trTypes.find(t => t.id.toString() === typeId.toString()) || null : null;

    let subtypeMode: SubtypeMode = null;
    let subtypeValue: TaxReturnSubType = null;
    let subtypeChain: TaxReturnSubType[] = null;
    const subtypeData = this.trSubtypesCtrl.value.value;
    if (subtypeData === SUBTYPE_PRIOR) {
      subtypeMode = SubtypeMode.Prior;
    } else if (subtypeData === TYPE_SUBTYPE_SAME) {
      subtypeMode = SubtypeMode.Same;
    } else if (subtypeData) {
      const isChain = (subtypeData.indexOf('_c') !== -1);
      if (isChain) {
        const chainOfIds = subtypeData.split('_')[0].split(',');
        subtypeMode = SubtypeMode.Chain;
        subtypeChain = chainOfIds.map((id => this.trSubtypes.find(st => st.id.toString() === id.toString())));
      } else { // Specific case
        const subtypeId = parseInt(subtypeData, 10);
        subtypeMode = SubtypeMode.Specific;
        subtypeValue = this.trSubtypes.find(st => st.id.toString() === subtypeId.toString()) || null;
      }
    }

    const isGlobalCalcPlan = this.originalCalcPlan.question.taxReturnTypeId === null;
    const sameType = isGlobalCalcPlan ? true : type && type.id === this.originalCalcPlan.question.taxReturnTypeId;
    // Fallback be Same Subtype when sameType otherwise Specific case for the first subtype
    if (!subtypeMode || !subtypeValue) {
      if (!sameType) {
        subtypeMode = SubtypeMode.Specific;
        subtypeValue = this.trSubtypes[0];
      }
    }

    return {
      priorYear,
      // if sameType then we pass in type = null so app-calc-input will display "Same Type" instead of the tax return type id
      type: sameType ? null : type,
      subtypeMode,
      subtypeValue,
      subtypeChain,
    };
  }

  loadTaxReturnSourceOptionsDropdowns(isGlobal = false) {
    if (isGlobal) {
      this.trYears = [this.currentYearConstant()];
      this.trTypeOptions = [this.sameTypeConstant()];
      this.trSubtypeOptions = [this.sameSubtypeConstant()];

      this.trYearsCtrl.setValue(this.trYears[0]);
      this.trTypesCtrl.setValue(this.trTypeOptions[0]);
      this.trSubtypesCtrl.setValue(this.trSubtypeOptions[0]);
      return;
    }

    this.trYears = [this.currentYearConstant(), this.priorYearConstant()];

    // Defaults to CY
    this.trYearsCtrl.setValue(this.trYears[0]);
    this.subscriptions.push(this.trTypesCtrl.valueChanges
      .pipe(
        switchMap((taxReturnType: any) => {
          const taxReturnSubtypes = this.typeSubtypeLookup.getAvailableSubtypesForTypeId(taxReturnType.id);
          return of({ taxReturnSubtypes });
        })
      ).subscribe(({ taxReturnSubtypes }) => {
        this.trSubtypes = taxReturnSubtypes;

        const result = taxReturnSubtypes.map((st, index) => (subtypeToDropdownListOptions(st, index)));
        const numSubtypes = result.length;

        const isSameType = this.trTypesCtrl.value.id === this.calcPlan.question.taxReturnTypeId;
        if (isSameType) {
          if (numSubtypes > 1) {
            result.push(this.sameSubtypeConstant());
          } else {
            result[0] = this.sameSubtypeConstant();
          }
          const chains = this.generateChains(taxReturnSubtypes);
          const offset = result.length;
          if (chains.length) {
            result.push(this.priorSubtypeConstant());
          }
          chains.forEach((c, idx) => {
            result.push(subtypeChainToDropdownListOption(c, offset + idx));
          });
        }

        const subtypeToPreselect = isSameType ? result.find(s => s.name === "Same Subtype") : result[0];

        console.log(`Preselect SUBTYPE: ${JSON.stringify(subtypeToPreselect)}`);

        this.trSubtypesCtrl.setValue(subtypeToPreselect);
        this.trSubtypeOptions = result;
      })
    );

    this.trTypes = this.typeSubtypeLookup.getAvailableTypes();
    this.trTypeOptions = this.trTypes.map((t, index) => (typeToDropdownListOptions(t, index)));
    const typeToPreselect = this.trTypeOptions.find(t => t.id === this.calcPlan?.question?.taxReturnTypeId) || this.trTypeOptions[0];
    this.trTypesCtrl.setValue(typeToPreselect);
  }

  private generateChains(taxReturnSubtypes: TaxReturnSubType[]): SubtypeChain[] {
    const subtypeChains = generateSubtypeChains(taxReturnSubtypes);
    const result = subtypeChains.filter(c => {
      if (!c.isLongest) { return false; }
      if (c.length <= 1) { return false; }
      return true;
    });
    return result;
  }

  private currentYearConstant() {
    return {
      id: 1,
      name: 'Current year',
      value: 'current year',
      order: 1,
      selected: true,
    };
  }

  private priorYearConstant() {
    return {
      id: 2,
      name: 'Prior year',
      value: 'prior year',
      order: 2,
      selected: false,
    };
  }

  private sameTypeConstant() {
    return {
      id: -1,
      name: "Same Type",
      value: TYPE_SUBTYPE_SAME,
      order: 2,
      selected: false,
    };
  }

  private sameSubtypeConstant() {
    return {
      id: -1,
      name: "Same Subtype",
      value: TYPE_SUBTYPE_SAME,
      order: 2,
      selected: false,
    };
  }

  private priorSubtypeConstant() {
    return {
      id: -2,
      name: "Prior in same chain",
      value: SUBTYPE_PRIOR,
      order: 3,
      selected: false,
    };
  }
}
